
import React from 'react';
import { TariffCardsGrid } from './TariffCardsGrid';
import { useTariffSelection } from './useTariffSelection';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { tariffs } from './tariff.constants';
import { useNavigate } from 'react-router-dom';

const TariffPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    selected, 
    setSelected, 
    sendTariff, 
    loading, 
    responseStatus,
    retryRequest
  } = useTariffSelection({
    onSuccess: () => {
      // После успешного выбора тарифа перенаправляем пользователя на главный экран после небольшой задержки
      setTimeout(() => {
        toast.success("Возвращаемся на главный экран");
        navigate('/courier');
      }, 2500);
    }
  });
  
  const handleContinue = async () => {
    if (!selected) {
      toast.error("Пожалуйста, выберите тариф");
      return;
    }
    
    const selectedTariff = tariffs.find(t => t.key === selected);
    if (selectedTariff) {
      if (responseStatus === 'timeout' || responseStatus === 'error') {
        retryRequest(selectedTariff);
      } else {
        await sendTariff(selectedTariff);
      }
    }
  };
  
  const handleBack = () => {
    navigate('/courier');
  };

  // Определяем текст кнопки в зависимости от статуса запроса
  const getButtonText = () => {
    if (loading) {
      if (responseStatus === 'sending') return 'Отправка запроса...';
      return 'Обработка...';
    }
    
    if (responseStatus === 'timeout') return 'Повторить запрос';
    if (responseStatus === 'error') return 'Попробовать снова';
    
    return 'Продолжить';
  };

  // Добавляем иконку в зависимости от статуса запроса
  const getButtonIcon = () => {
    if (loading) return <Loader2 className="ml-2 h-5 w-5 animate-spin" />;
    if (responseStatus === 'timeout') return <Clock className="ml-2 h-5 w-5" />;
    if (responseStatus === 'error') return <AlertCircle className="ml-2 h-5 w-5" />;
    return <ArrowRight className="ml-2 h-5 w-5" />;
  };
  
  return (
    <div className="space-y-6 pb-16">
      {/* Кнопка "Назад" для возврата на основной экран */}
      <div className="flex items-center mb-2">
        <Button 
          onClick={handleBack} 
          variant="outline"
          className="flex items-center gap-1 text-sm"
          size="sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Назад
        </Button>
      </div>
      
      <h1 className="text-2xl font-bold">Выбор тарифа</h1>
      <p className="text-muted-foreground">Выберите подходящий тариф для аренды электровелосипеда</p>
      
      <TariffCardsGrid 
        selected={selected} 
        setSelected={setSelected} 
      />
      
      {/* Сообщения о статусе запроса */}
      {responseStatus === 'timeout' && (
        <div className="p-4 bg-yellow-500/20 border border-yellow-400/40 rounded-lg">
          <p className="text-sm flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-400" />
            <span>Превышено время ожидания ответа от сервера. Пожалуйста, попробуйте снова.</span>
          </p>
        </div>
      )}
      
      {responseStatus === 'error' && (
        <div className="p-4 bg-red-500/20 border border-red-400/40 rounded-lg">
          <p className="text-sm flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <span>Произошла ошибка при обработке запроса. Пожалуйста, попробуйте позже.</span>
          </p>
        </div>
      )}
      
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/80 backdrop-blur-sm border-t">
        <div className="max-w-[900px] mx-auto">
          <Button 
            onClick={handleContinue}
            disabled={!selected || (loading && responseStatus !== 'timeout' && responseStatus !== 'error')}
            className="w-full py-2 sm:py-6 text-base sm:text-lg font-medium"
            variant="default"
          >
            {getButtonText()} 
            {getButtonIcon()}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TariffPage;
